import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { WorkspaceAssignmentCreateBody, WorkspaceAssignmentUpdateBody } from '@/domain/workflow-admin.types'
import { workflowAdminApi } from '@/services/workflow.service'

/** React Query hooks for the builder's per-status workspace-assignment CRUD (`workspace_assignment`, design.md D5). */

const ROOT = 'workspace-assignment-admin'

export const workspaceAssignmentAdminKeys = {
	forStatus: (statusId: string | null) => [ROOT, statusId] as const,
}

export const useWorkspaceAssignments = (statusId: string | null) =>
	useQuery({
		queryKey: workspaceAssignmentAdminKeys.forStatus(statusId),
		queryFn: () => workflowAdminApi.listWorkspaceAssignments(statusId as string),
		enabled: !!statusId,
	})

export const useCreateWorkspaceAssignment = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (body: WorkspaceAssignmentCreateBody) => workflowAdminApi.createWorkspaceAssignment(body),
		onSuccess: (_data, body) =>
			void qc.invalidateQueries({ queryKey: workspaceAssignmentAdminKeys.forStatus(body.statusId) }),
	})
}

export const useUpdateWorkspaceAssignment = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({
			assignmentId,
			body,
		}: {
			assignmentId: string
			statusId: string
			body: WorkspaceAssignmentUpdateBody
		}) => workflowAdminApi.updateWorkspaceAssignment(assignmentId, body),
		onSuccess: (_data, { statusId }) =>
			void qc.invalidateQueries({ queryKey: workspaceAssignmentAdminKeys.forStatus(statusId) }),
	})
}

export const useDeleteWorkspaceAssignment = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ assignmentId }: { assignmentId: string; statusId: string }) =>
			workflowAdminApi.deleteWorkspaceAssignment(assignmentId),
		onSuccess: (_data, { statusId }) =>
			void qc.invalidateQueries({ queryKey: workspaceAssignmentAdminKeys.forStatus(statusId) }),
	})
}
